import { createFileRoute } from "@tanstack/react-router";
import {
	CompanyDetails,
	LegalPage,
	type LegalSection,
} from "@/components/legal/legal-page";

export const Route = createFileRoute("/privacy")({
	component: PrivacyPage,
	head: () => ({
		meta: [
			{ title: "Privacy Policy — Proxi" },
			{
				name: "description",
				content:
					"How Proxi collects, uses, shares, and protects personal data for customers and runners in Nigeria.",
			},
			{ property: "og:title", content: "Privacy Policy — Proxi" },
			{
				property: "og:description",
				content:
					"How Proxi handles personal data for its customer and runner services.",
			},
			{ property: "og:url", content: "https://myproxi.ng/privacy" },
			{ name: "twitter:title", content: "Privacy Policy — Proxi" },
			{
				name: "twitter:description",
				content:
					"How Proxi handles personal data for its customer and runner services.",
			},
		],
		links: [{ rel: "canonical", href: "https://myproxi.ng/privacy" }],
	}),
});

const sections: LegalSection[] = [
	{
		id: "scope",
		heading: "Who we are and what this policy covers",
		body: (
			<>
				<p>
					This policy explains how Proxi handles personal data when you visit
					our website, join our waitlist, use the customer or runner app,
					contact support, or otherwise interact with our services.
				</p>
				<CompanyDetails />
			</>
		),
	},
	{
		id: "data-collected",
		heading: "Personal data we collect",
		body: (
			<>
				<p>Depending on how you use Proxi, we may collect:</p>
				<ul>
					<li>
						<strong>Account and contact data:</strong> name, email address,
						phone number, profile photograph, role, and verification status.
					</li>
					<li>
						<strong>Errand and location data:</strong> task descriptions, pickup
						and delivery details, precise or approximate location, routes,
						status updates, messages, photographs, receipts, and completion
						records.
					</li>
					<li>
						<strong>Payment data:</strong> transaction references, wallet
						activity, agreed prices, refunds, payouts, bank account details, and
						payment status. Payment partners process card or bank credentials
						under their own privacy terms; Proxi does not store full card
						details.
					</li>
					<li>
						<strong>Runner verification data:</strong> identity and eligibility
						information, including identity document details, NIN or BVN where
						required, work and vehicle details, references, guarantor or
						next-of-kin information, and review outcomes.
					</li>
					<li>
						<strong>Device and usage data:</strong> device type, operating
						system, app version, IP address, identifiers, crash reports,
						security events, pages or screens viewed, and interaction data.
					</li>
					<li>
						<strong>Support and research data:</strong> support requests,
						attachments, feedback, survey responses, and communications with our
						team.
					</li>
				</ul>
			</>
		),
	},
	{
		id: "sources",
		heading: "Where the data comes from",
		body: (
			<p>
				We collect data directly from you, automatically from your device when
				you use Proxi, from the customer or runner participating in the same
				errand, and from service providers that support identity, payments,
				communications, analytics, security, and support. If you give us another
				person's data, such as a recipient or guarantor, you must have their
				permission to do so.
			</p>
		),
	},
	{
		id: "use",
		heading: "How we use personal data",
		body: (
			<>
				<p>We use personal data to:</p>
				<ul>
					<li>create, verify, secure, and support accounts;</li>
					<li>
						match customers and runners and provide the errand workflow,
						location features, messages, and completion records;
					</li>
					<li>
						process wallet funding, protected payments, refunds, and runner
						payouts;
					</li>
					<li>
						send requested one-time passwords, delivery confirmation codes,
						receipts, security alerts, and errand status notifications;
					</li>
					<li>
						prevent fraud, investigate incidents, resolve disputes, enforce our
						terms, and protect users;
					</li>
					<li>
						maintain, troubleshoot, measure, and improve our apps and services;
					</li>
					<li>
						comply with legal, regulatory, accounting, and tax obligations.
					</li>
				</ul>
				<p>
					Where required, we rely on your consent. We also process data where it
					is necessary to provide the service you request, comply with law, or
					pursue a legitimate interest such as preventing fraud and keeping
					Proxi secure, provided your rights do not override that interest.
				</p>
			</>
		),
	},
	{
		id: "sharing",
		heading: "When we share personal data",
		body: (
			<>
				<p>We share data only where it is needed for the purposes above:</p>
				<ul>
					<li>
						<strong>Between errand participants.</strong> Customers and runners
						receive the information needed to coordinate and complete an errand.
						We limit sensitive pickup or delivery details until the workflow
						requires them.
					</li>
					<li>
						<strong>With service providers.</strong> Providers support cloud
						hosting, authentication, SMS and email delivery, payments, maps,
						analytics, error monitoring, customer support, and identity or fraud
						checks. They may process data only for the contracted service and
						under appropriate safeguards.
					</li>
					<li>
						<strong>For legal and safety reasons.</strong> We may disclose data
						when required by law or reasonably necessary to protect a person,
						investigate fraud, enforce agreements, or respond to a valid legal
						request.
					</li>
					<li>
						<strong>In a business transaction.</strong> If Proxi is involved in
						a financing, merger, acquisition, reorganisation, or sale, data may
						be shared subject to confidentiality and applicable law.
					</li>
				</ul>
				<p>We do not sell personal data.</p>
			</>
		),
	},
	{
		id: "messaging",
		heading: "SMS and service communications",
		body: (
			<>
				<p>
					When you provide and verify a phone number, we may send transactional
					messages needed to deliver Proxi. These include authentication codes,
					delivery confirmation codes, security notices, and updates about an
					errand involving you.
				</p>
				<p>
					We do not use these transactional messages for bulk promotions. You
					may be unable to use phone-dependent features if you block essential
					service messages. Contact support if you receive a message you did not
					expect.
				</p>
			</>
		),
	},
	{
		id: "retention",
		heading: "How long we keep data",
		body: (
			<p>
				We keep personal data only as long as reasonably necessary for the
				purposes described here. The period depends on the type of data, whether
				your account or a transaction remains active, fraud and safety risks,
				dispute and support needs, and legal, tax, accounting, or regulatory
				requirements. We then delete or anonymise the data unless law requires
				continued retention.
			</p>
		),
	},
	{
		id: "security-transfers",
		heading: "Security and international processing",
		body: (
			<>
				<p>
					We use technical and organisational safeguards designed to protect
					personal data, including access controls, authentication, logging, and
					encrypted transmission where appropriate. No online service can
					guarantee absolute security, so keep verification codes and account
					credentials private and tell us about suspected misuse promptly.
				</p>
				<p>
					Some providers may process data outside Nigeria. Where personal data
					is transferred internationally, we use a lawful transfer mechanism and
					safeguards appropriate to the destination and service.
				</p>
			</>
		),
	},
	{
		id: "rights",
		heading: "Your privacy rights",
		body: (
			<>
				<p>
					Subject to applicable law, including the Nigeria Data Protection Act
					2023, you may ask us to:
				</p>
				<ul>
					<li>
						confirm whether we process your data and provide access to it;
					</li>
					<li>correct inaccurate or incomplete data;</li>
					<li>delete data or restrict how it is used;</li>
					<li>provide portable data where the right applies;</li>
					<li>object to certain processing or withdraw consent;</li>
					<li>
						ask for human review of a decision based solely on automated
						processing, where applicable.
					</li>
				</ul>
				<p>
					Email <a href="mailto:info@myproxi.ng">info@myproxi.ng</a> to make a
					request. We may need to verify your identity. Some rights are subject
					to legal exceptions, including records we must retain. You may also
					complain to the Nigeria Data Protection Commission.
				</p>
			</>
		),
	},
	{
		id: "children-changes",
		heading: "Children and policy updates",
		body: (
			<>
				<p>
					Proxi is not intended for anyone under 18, and we do not knowingly
					create accounts for children. Contact us if you believe a child has
					provided personal data through the service.
				</p>
				<p>
					We may update this policy as our services or legal obligations change.
					We will post the new version here, update the date above, and provide
					reasonable notice if a change materially affects how we use personal
					data.
				</p>
			</>
		),
	},
];

function PrivacyPage() {
	return (
		<LegalPage
			eyebrow="Privacy"
			title="Privacy Policy"
			summary="What Proxi collects, why we need it, and the choices you have about your data."
			lastUpdated="21 August 2026"
			sections={sections}
		/>
	);
}
